import {
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Icon,
  IconButton,
  Spacer,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr
} from '@chakra-ui/react'
import fileDownload from 'js-file-download'
import { FC, useEffect, useMemo, useState } from 'react'
import {
  MdAddCircleOutline,
  MdDeleteOutline,
  MdOutlineEdit
} from 'react-icons/md'

import useFollowUpConfig from '@hook/followUpFornecedor/use-config'
import useFilters from '@hook/shared/use-filters'
import useLayoutHeight from '@hook/shared/use-layout-height'

import MultiItemCell from '@component/FollowUpFornecedor/MultiItemCell'
import Layout from '@component/Layout'
import LayoutEmpty from '@component/Layout/Empty'
import LayoutLoader from '@component/Layout/Loader'
import LayoutTools from '@component/Layout/Tools'

import {
  ICreateConfigPayload,
  IEditConfigPayload,
  IFollowUpConfig
} from '@base/@types/providers/FollowUpFornecedor/ConfigProvider'

import CreateModal from './CreateModal'
import EditModal from './EditModal'

interface IGroupedConfig {
  grupoId: string
  emails: string[]
  suppliers: { id: number; description: string }[]
  stores: { id: number; description: string }[]
  raw: IFollowUpConfig
}

const FollowUpFornecedorConfig: FC = () => {
  const height = useLayoutHeight()

  const {
    loading,
    data,
    modalCreate,
    modalEdit,
    getData,
    handleOpenCreate,
    handleCloseCreate,
    handleOpenEdit,
    handleCloseEdit,
    handleCreate,
    handleUpdate,
    handleDelete
  } = useFollowUpConfig()

  const {
    providersForPurchase,
    centers,
    getFilters,
    loading: loadingFilters
  } = useFilters()

  const [form, setForm] = useState<ICreateConfigPayload>({
    emails: [],
    supplierIds: [],
    storeIds: []
  })
  const [editForm, setEditForm] = useState<IEditConfigPayload>({
    emails: [],
    supplierIds: [],
    storeIds: []
  })
  const [query, setQuery] = useState('')

  useEffect(() => {
    getData()
    getFilters({ providersForPurchase: true, centers: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (modalEdit) {
      setEditForm({
        emails: modalEdit.emails,
        supplierIds: modalEdit.supplierIds,
        storeIds: modalEdit.storeIds
      })
    }
  }, [modalEdit])

  const grouped = useMemo<IGroupedConfig[]>(() => {
    const map = new Map<string, IGroupedConfig>()
    data.forEach(item => {
      const key = item.grupoId
      if (!key) return
      if (!map.has(key)) {
        map.set(key, {
          grupoId: key,
          emails: [],
          suppliers: [],
          stores: [],
          raw: item
        })
      }
      const g = map.get(key)!
      if (item.email && !g.emails.includes(item.email)) {
        g.emails.push(item.email)
      }
      const sid = Number(item.supplierId)
      if (!g.suppliers.some(s => s.id === sid)) {
        g.suppliers.push({ id: sid, description: item.supplierDescription })
      }
      const cid = Number(item.storeId)
      if (!g.stores.some(s => s.id === cid)) {
        g.stores.push({ id: cid, description: item.storeDescription })
      }
    })
    return Array.from(map.values())
  }, [data])

  const filtered = useMemo(
    () =>
      query
        ? grouped.filter(g =>
            JSON.stringify(g).toLowerCase().includes(query.toLowerCase())
          )
        : grouped,
    [grouped, query]
  )

  const handleExport = () => {
    const header = 'E-mail;Fornecedor;Cód. Fornecedor;Centro;Status'
    const rows = data.map(i =>
      [
        `"${i.email}"`,
        `"${i.supplierDescription}"`,
        i.supplierId,
        `"${i.storeDescription}"`,
        i.isActive ? 'Ativo' : 'Inativo'
      ].join(';')
    )
    fileDownload(
      '\uFEFF' + [header, ...rows].join('\r\n'),
      'follow-up-config.csv',
      'text/csv;charset=utf-8'
    )
  }

  const actions = useMemo(
    () => [
      {
        type: 'export' as const,
        exports: [
          { type: 'excel' as const, data: filtered, onClick: handleExport }
        ]
      },
      { type: 'followUpFornecedorConfigHistoric' as const }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtered]
  )

  const supplierOptions = useMemo(
    () =>
      providersForPurchase?.map((s: any) => ({
        value: s.value,
        label: s.label
      })) ?? [],
    [providersForPurchase]
  )

  const centerOptions = useMemo(
    () => centers?.map((c: any) => ({ value: c.value, label: c.label })) ?? [],
    [centers]
  )

  return (
    <Layout title="Follow Up Fornecedor — Configurações">
      <Grid w="100vw" bg="white" h={height} templateColumns="100vw">
        <Box>
          <LayoutTools
            title="Configuração de E-mails"
            actions={actions}
            query={query}
            onSearchChange={setQuery}
            isDisabled={loading || data.length === 0}
          />

          <Flex
            borderBottom="solid 1px"
            borderColor="gray.200"
            alignItems="center"
            px={5}
            py={3}
          >
            <Spacer />
            <Button
              size="md"
              bg="#1d5840"
              color="white"
              fontWeight="600"
              px={5}
              leftIcon={<Icon as={MdAddCircleOutline} boxSize={5} />}
              borderRadius="lg"
              _hover={{ bg: '#174a35' }}
              _active={{ bg: '#123c2b' }}
              onClick={handleOpenCreate}
            >
              Nova Configuração
            </Button>
          </Flex>

          {loading ? (
            <LayoutLoader
              title="Recuperando informações..."
              message="Aguarde enquanto buscamos os dados no banco de dados"
            />
          ) : data.length === 0 ? (
            <LayoutEmpty
              title="Nenhuma configuração encontrada"
              message="Clique em '+ Nova Configuração' para começar"
            />
          ) : (
            <Box overflow="auto" px={5} py={4}>
              <Box
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="lg"
                overflow="hidden"
              >
                <Table size="sm" className="scl-table-grid">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Fornecedor(es)</Th>
                      <Th>Centro(s)</Th>
                      <Th>E-mail(s)</Th>
                      <Th>Ações</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filtered.map(group => (
                      <Tr key={group.grupoId}>
                        <Td>
                          <MultiItemCell
                            items={group.suppliers.map(s => s.description)}
                          />
                        </Td>
                        <Td>
                          <MultiItemCell
                            items={group.stores.map(s => s.description)}
                          />
                        </Td>
                        <Td>
                          <MultiItemCell items={group.emails} />
                        </Td>
                        <Td>
                          <HStack spacing={1}>
                            <Tooltip label="Editar" hasArrow placement="top">
                              <IconButton
                                aria-label="Editar"
                                icon={<Icon as={MdOutlineEdit} boxSize={4} />}
                                size="xs"
                                bg="blue.500"
                                color="white"
                                _hover={{ bg: 'blue.600' }}
                                _active={{ bg: 'blue.700' }}
                                onClick={() => handleOpenEdit(group.raw)}
                              />
                            </Tooltip>
                            <Tooltip label="Remover" hasArrow placement="top">
                              <IconButton
                                aria-label="Remover"
                                icon={<Icon as={MdDeleteOutline} boxSize={4} />}
                                size="xs"
                                bg="red.500"
                                color="white"
                                _hover={{ bg: 'red.600' }}
                                _active={{ bg: 'red.700' }}
                                onClick={() => handleDelete(group.grupoId)}
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          )}
        </Box>
      </Grid>

      <CreateModal
        isOpen={modalCreate}
        form={form}
        setForm={setForm}
        supplierOptions={supplierOptions}
        centerOptions={centerOptions}
        loadingFilters={loadingFilters}
        onClose={() => {
          handleCloseCreate()
          setForm({ emails: [], supplierIds: [], storeIds: [] })
        }}
        onCreate={async () => {
          await handleCreate(form)
          setForm({ emails: [], supplierIds: [], storeIds: [] })
        }}
      />

      <EditModal
        isOpen={!!modalEdit}
        editForm={editForm}
        setEditForm={setEditForm}
        supplierOptions={supplierOptions}
        centerOptions={centerOptions}
        loadingFilters={loadingFilters}
        onClose={handleCloseEdit}
        onUpdate={() => handleUpdate(editForm)}
      />
    </Layout>
  )
}

export default FollowUpFornecedorConfig
